// CHG-1285 / ITER-3410 — v2 Templates editor.
//
// Closes the CHG-1261 view-only forward-promise. The CHG-1261 EditorPreview
// rendered the latest version's subject + body as read-only; this component
// adds a view→edit toggle, Subject + Body + Tone + Status fields, Save/Discard
// buttons, FE validation matching backend constraints, and an optimistic
// update pattern with rollback on PATCH failure.
//
// Field primitive choices:
//   - Subject:  native <input type="text"> (TextField primitive not yet
//                shipped per CHG-1246; forward-promise to TextField when it lands)
//   - Body:     native <textarea> (RichTextField primitive is a CHG-1167 stub
//                rendering a single-line <input>; forward-promise to RichTextField
//                when it gains a multi-line rich-text impl)
//   - Tone:     native <select> over TemplateTone enum (SelectField primitive
//                not yet shipped per CHG-1246)
//   - Status:   native <select> over TemplateStatus enum
// Each field has an explicit comment tying it to the canonical primitive.
// Same minimal-viable-port pattern as CHG-1277 auth pages.
//
// Brand chrome wrapper preserved from the CHG-1271 surface-5 EditorPreview.
//
// Backend: PATCH /templates/{id} — itself forward-promised. The wrapper at
// api/templates.ts::updateTemplate calls the endpoint; this component surfaces
// the typed Error in a friendly "Edits will persist when backend ships" notice
// when the call 404s today.

import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { useBrand } from "../../contexts/BrandContext";
import { templatesApi, type TemplateUpdatePatch } from "../../api/templates";
import type {
  Template,
  TemplateStatus,
  TemplateTone,
  TemplateVersion,
} from "../../api/types";

// FE validation constants — must match backend per
// feedback_fe_be_validation_must_match.md. Backend
// NotificationTemplateUpdate.subject is max_length=500; FE caps at 200
// for UX (longer subjects rarely render correctly in email clients).
export const SUBJECT_MAX_LENGTH = 200;
export const BODY_MAX_LENGTH = 50_000;

const TONES: readonly TemplateTone[] = [
  "warm",
  "urgent",
  "celebratory",
  "concerned",
  "grateful",
];
const STATUSES: readonly TemplateStatus[] = ["draft", "published", "archived"];
const TONE_LABEL: Record<TemplateTone, string> = {
  warm: "Warm",
  urgent: "Urgent",
  celebratory: "Celebratory",
  concerned: "Concerned",
  grateful: "Grateful",
};
const STATUS_LABEL: Record<TemplateStatus, string> = {
  draft: "Draft",
  published: "Published",
  archived: "Archived",
};

interface FormState {
  subject: string;
  body: string;
  tone: TemplateTone;
  status: TemplateStatus;
}

interface FieldErrors {
  subject?: string;
  body?: string;
  tone?: string;
  status?: string;
  server?: string;
}

function validate(form: FormState): FieldErrors {
  const errs: FieldErrors = {};
  if (!form.subject.trim()) {
    errs.subject = "Subject is required.";
  } else if (form.subject.length > SUBJECT_MAX_LENGTH) {
    errs.subject = `Subject must be ${SUBJECT_MAX_LENGTH} characters or fewer.`;
  }
  if (!form.body.trim()) {
    errs.body = "Body is required.";
  } else if (form.body.length > BODY_MAX_LENGTH) {
    errs.body = `Body must be ${BODY_MAX_LENGTH} characters or fewer.`;
  }
  if (!TONES.includes(form.tone)) {
    errs.tone = "Pick a tone.";
  }
  if (!STATUSES.includes(form.status)) {
    errs.status = "Pick a status.";
  }
  return errs;
}

interface Props {
  template: Template;
  /** Permission gate — if false, edit affordance is hidden and the editor
   * stays in view mode. The parent (template-detail generator slot) reads
   * `alora-giving.template.update` and passes it through. */
  canEdit?: boolean;
}

type Phase = "view" | "edit" | "saving";

export function TemplateEditor({ template, canEdit = true }: Props) {
  const { chrome } = useBrand();
  const [phase, setPhase] = useState<Phase>("view");
  const [versionState, setVersionState] = useState<{
    loading: boolean;
    error: Error | null;
    version: TemplateVersion | null;
  }>({ loading: true, error: null, version: null });

  // Form state reflects the latest version's subject + body + the template's
  // tone + status. Re-initialized when the version loads.
  const [form, setForm] = useState<FormState>({
    subject: "",
    body: "",
    tone: template.tone,
    status: template.status,
  });
  const [originalForm, setOriginalForm] = useState<FormState>(form);
  const [errors, setErrors] = useState<FieldErrors>({});

  useEffect(() => {
    let cancelled = false;
    templatesApi.listTemplateVersions(template.id).then(
      (r) => {
        if (cancelled) return;
        const v = r.items[0] ?? null;
        const next: FormState = {
          subject: v?.subject ?? "",
          body: v?.body ?? "",
          tone: template.tone,
          status: template.status,
        };
        setForm(next);
        setOriginalForm(next);
        setVersionState({ loading: false, error: null, version: v });
      },
      (err) => {
        if (cancelled) return;
        setVersionState({ loading: false, error: err, version: null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [template.id, template.tone, template.status]);

  const isDirty = useMemo(
    () =>
      form.subject !== originalForm.subject ||
      form.body !== originalForm.body ||
      form.tone !== originalForm.tone ||
      form.status !== originalForm.status,
    [form, originalForm],
  );

  async function onSave() {
    const errs = validate(form);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setPhase("saving");
    const patch: TemplateUpdatePatch = {};
    if (form.subject !== originalForm.subject) patch.subject = form.subject;
    if (form.body !== originalForm.body) patch.body = form.body;
    if (form.tone !== originalForm.tone) patch.tone = form.tone;
    if (form.status !== originalForm.status) patch.status = form.status;
    // Optimistic UI: bake the new values into "original" so the
    // "dirty" indicator clears immediately. On error we roll back.
    const optimisticOriginal = { ...form };
    setOriginalForm(optimisticOriginal);
    try {
      await templatesApi.updateTemplate(template.id, patch);
      setPhase("view");
    } catch (e: unknown) {
      // Roll back the optimistic baseline so the form remains dirty
      // (user can retry or discard).
      setOriginalForm(originalForm);
      const detail = extractErrorDetail(e);
      setErrors({ server: detail });
      setPhase("edit");
    }
  }

  function onDiscard() {
    setForm(originalForm);
    setErrors({});
    setPhase("view");
  }

  if (versionState.loading) {
    return (
      <div data-component="template-editor" className="card" style={loadingStyle}>
        Loading editor…
      </div>
    );
  }

  return (
    <div
      data-component="template-editor"
      data-brand-frame="template-editor"
      data-mode={phase === "view" ? "view" : "edit"}
      style={{ display: "flex", flexDirection: "column", gap: 12 }}
    >
      {/* Brand chrome strip — preserved from CHG-1271 surface-5 EditorPreview */}
      <div data-brand-strip="template-editor-header" style={brandStripStyle}>
        <span className="font-wordmark" style={wordmarkStyle}>
          {chrome.wordmark}
        </span>
        <span style={{ color: "var(--color-text-subtle)" }}>·</span>
        <span>{chrome.tagline}</span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {phase === "view" && canEdit && (
            <button
              type="button"
              data-action="edit"
              onClick={() => setPhase("edit")}
              style={primaryButton}
            >
              Edit
            </button>
          )}
          {phase !== "view" && (
            <>
              <button
                type="button"
                data-action="discard"
                onClick={onDiscard}
                disabled={phase === "saving"}
                style={secondaryButton}
              >
                Discard
              </button>
              <button
                type="button"
                data-action="save"
                onClick={onSave}
                disabled={phase === "saving" || !isDirty}
                style={primaryButton}
              >
                {phase === "saving" ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </div>
      </div>

      {phase === "view" ? (
        <ViewMode version={versionState.version} template={template} />
      ) : (
        <EditMode
          form={form}
          errors={errors}
          disabled={phase === "saving"}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ViewMode — read-only preview (subject + body + variables)
// ─────────────────────────────────────────────────────────────────

function ViewMode({ version, template }: { version: TemplateVersion | null; template: Template }) {
  if (!version) {
    return (
      <div className="card" style={emptyStyle}>
        No version available — template hasn't been saved yet. Click Edit to author the first version.
      </div>
    );
  }
  return (
    <>
      <Card label="Subject">
        <div style={{ fontSize: 14, fontWeight: 500 }}>
          {version.subject ? (
            version.subject
          ) : (
            <span style={{ color: "var(--color-text-muted)" }}>
              (no subject — SMS or in-app body only)
            </span>
          )}
        </div>
      </Card>
      <Card label={`Body · version ${version.version} · view-only`}>
        <pre style={preStyle}>{version.body}</pre>
      </Card>
      {version.variables.length > 0 && (
        <div
          className="card"
          style={{ padding: 16, fontSize: 12, color: "var(--color-text-muted)" }}
        >
          Variables: {version.variables.join(", ")}
        </div>
      )}
      <div
        data-role="template-meta"
        style={{ fontSize: 12, color: "var(--color-text-muted)", padding: "0 4px" }}
      >
        Tone: <strong>{TONE_LABEL[template.tone]}</strong> · Status:{" "}
        <strong>{STATUS_LABEL[template.status]}</strong>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// EditMode — form with subject + body + tone + status
// ─────────────────────────────────────────────────────────────────

interface EditModeProps {
  form: FormState;
  errors: FieldErrors;
  disabled: boolean;
  onChange: (patch: Partial<FormState>) => void;
}

function EditMode({ form, errors, disabled, onChange }: EditModeProps) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {errors.server && (
        <div data-role="server-error" style={serverErrorStyle}>
          {errors.server}
        </div>
      )}

      {/* Subject — forward-promise to TextField primitive (CHG-1246 pending) */}
      <Card label="Subject *">
        <input
          type="text"
          data-field="subject"
          value={form.subject}
          maxLength={SUBJECT_MAX_LENGTH + 1 /* allow over-typing so validation can fire */}
          disabled={disabled}
          onChange={(e) => onChange({ subject: e.target.value })}
          style={inputStyle(!!errors.subject)}
          aria-invalid={!!errors.subject}
        />
        <FieldMeta
          error={errors.subject}
          hint={`${form.subject.length} / ${SUBJECT_MAX_LENGTH}`}
        />
      </Card>

      {/* Body — forward-promise to RichTextField primitive (CHG-1156 / CHG-1246 pending) */}
      <Card label="Body *">
        <textarea
          data-field="body"
          value={form.body}
          disabled={disabled}
          onChange={(e) => onChange({ body: e.target.value })}
          rows={14}
          style={textareaStyle(!!errors.body)}
          aria-invalid={!!errors.body}
        />
        <FieldMeta
          error={errors.body}
          hint={`${form.body.length} / ${BODY_MAX_LENGTH}`}
        />
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {/* Tone — forward-promise to SelectField primitive (CHG-1246 pending) */}
        <Card label="Tone *">
          <select
            data-field="tone"
            value={form.tone}
            disabled={disabled}
            onChange={(e) => onChange({ tone: e.target.value as TemplateTone })}
            style={selectStyle(!!errors.tone)}
            aria-invalid={!!errors.tone}
          >
            {TONES.map((t) => (
              <option key={t} value={t}>
                {TONE_LABEL[t]}
              </option>
            ))}
          </select>
          <FieldMeta error={errors.tone} />
        </Card>

        {/* Status — same SelectField forward-promise */}
        <Card label="Status *">
          <select
            data-field="status"
            value={form.status}
            disabled={disabled}
            onChange={(e) => onChange({ status: e.target.value as TemplateStatus })}
            style={selectStyle(!!errors.status)}
            aria-invalid={!!errors.status}
          >
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
          <FieldMeta error={errors.status} />
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────────

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={cardLabelStyle}>{label}</div>
      {children}
    </div>
  );
}

function FieldMeta({ error, hint }: { error?: string; hint?: string }) {
  if (!error && !hint) return null;
  return (
    <div
      data-role="field-meta"
      style={{
        marginTop: 6,
        display: "flex",
        justifyContent: "space-between",
        fontSize: 11,
        color: error ? "var(--color-error)" : "var(--color-text-muted)",
      }}
    >
      <span data-role={error ? "error" : "hint"}>{error ?? ""}</span>
      {hint && <span>{hint}</span>}
    </div>
  );
}

function extractErrorDetail(e: unknown): string {
  // Axios error shape — render the FastAPI `detail` field when available.
  const anyE = e as { response?: { status?: number; data?: { detail?: string } }; message?: string };
  const status = anyE?.response?.status;
  if (status === 404 || status === 501) {
    return "Edits will persist when the backend update endpoint ships (forward-promised). Your changes are still here — try again or discard.";
  }
  return anyE?.response?.data?.detail ?? anyE?.message ?? "Save failed. Try again.";
}

// ─────────────────────────────────────────────────────────────────
// Styles (inline; matches the _dev/auth-preview minimal-deps precedent)
// ─────────────────────────────────────────────────────────────────

const brandStripStyle: CSSProperties = {
  padding: "8px 16px",
  borderRadius: "var(--radius-md)",
  background: "var(--color-bg-subtle)",
  border: "1px solid var(--color-border-subtle)",
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 12,
  color: "var(--color-text-muted)",
};

const wordmarkStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--color-text)",
  fontFamily: "var(--font-wordmark, 'Poppins', sans-serif)",
};

const cardLabelStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: 8,
  fontWeight: 600,
};

const loadingStyle: CSSProperties = {
  padding: 16,
  color: "var(--color-text-muted)",
};

const emptyStyle: CSSProperties = {
  padding: 16,
  color: "var(--color-text-muted)",
};

const preStyle: CSSProperties = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.6,
  fontFamily: "var(--font-mono, monospace)",
  whiteSpace: "pre-wrap",
  wordWrap: "break-word",
  color: "var(--color-text)",
};

const serverErrorStyle: CSSProperties = {
  padding: "10px 14px",
  borderRadius: "var(--radius-md, 8px)",
  background: "var(--color-danger-bg, #fef2f2)",
  color: "var(--color-danger, #b91c1c)",
  fontSize: 13,
};

function inputStyle(hasError: boolean): CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    fontSize: 14,
    border: `1px solid ${hasError ? "var(--color-error)" : "var(--color-border)"}`,
    borderRadius: "var(--radius-sm, 6px)",
    background: "var(--color-bg-subtle)",
    color: "var(--color-text)",
    boxSizing: "border-box",
  };
}

function textareaStyle(hasError: boolean): CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    fontSize: 13,
    lineHeight: 1.55,
    fontFamily: "var(--font-mono, monospace)",
    border: `1px solid ${hasError ? "var(--color-error)" : "var(--color-border)"}`,
    borderRadius: "var(--radius-sm, 6px)",
    background: "var(--color-bg-subtle)",
    color: "var(--color-text)",
    boxSizing: "border-box",
    resize: "vertical",
  };
}

function selectStyle(hasError: boolean): CSSProperties {
  return {
    width: "100%",
    padding: "8px 10px",
    fontSize: 14,
    border: `1px solid ${hasError ? "var(--color-error)" : "var(--color-border)"}`,
    borderRadius: "var(--radius-sm, 6px)",
    background: "var(--color-bg-subtle)",
    color: "var(--color-text)",
    boxSizing: "border-box",
  };
}

const primaryButton: CSSProperties = {
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--radius-md, 8px)",
  background: "var(--color-primary, var(--ag-brand-primary, #6366F1))",
  color: "var(--color-on-primary, #fff)",
  cursor: "pointer",
};

const secondaryButton: CSSProperties = {
  padding: "6px 14px",
  fontSize: 13,
  fontWeight: 500,
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-md, 8px)",
  background: "transparent",
  color: "var(--color-text)",
  cursor: "pointer",
  fontFamily: "inherit",
};
